// auth.api.ts — 004-dual-login-2fa (révisé selon FRONTEND_AUTH_GUIDE.md)
// Fonctions API pour l'authentification locale (TOTP) et la réinitialisation de
// mot de passe. Contrats : FRONTEND_AUTH_GUIDE.md sections 4, 7, 8.

import type {
  LocalLoginRequest,
  LocalLoginResponse,
  MfaVerifyRequest,
  MfaVerifyResponse,
  MfaEnrollInitiateResponse,
  MfaEnrollConfirmRequest,
  MfaEnrollConfirmResponse,
  ResetPasswordRequest,
  ChangePasswordRequest,
  AcceptInvitationRequest,
} from "@/domain/types/auth";

import apiClient from "./client";
import localAuthClient from "./local-auth-client";

// ---------------------------------------------------------------------------
// Step 1 — Connexion email / mot de passe
// ---------------------------------------------------------------------------

/**
 * POST /api/auth/local/login — Anonymous, rate-limited.
 * Retourne un partialAuthToken (JWT, audience "partial-auth", TTL 5 min).
 * Stocker en mémoire JS UNIQUEMENT (jamais sessionStorage ni localStorage).
 */
export async function loginWithCredentials(
  body: LocalLoginRequest,
): Promise<LocalLoginResponse> {
  const { data } = await localAuthClient.post<LocalLoginResponse>(
    "/api/auth/local/login",
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Step 2 — Vérification TOTP
// ---------------------------------------------------------------------------

/**
 * POST /api/auth/local/mfa/verify — Requiert partialAuthToken en Authorization.
 * Retourne le JWT full-access (TTL 48 h) — stocker en sessionStorage.
 */
export async function verifyMfa(
  request: MfaVerifyRequest,
  partialAuthToken: string,
): Promise<MfaVerifyResponse> {
  const { data } = await localAuthClient.post<MfaVerifyResponse>(
    "/api/auth/local/mfa/verify",
    { code: request.code, isBackupCode: request.isBackupCode },
    { headers: { Authorization: `Bearer ${partialAuthToken}` } },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Enrollment MFA — premier login (isMfaEnrolled: false)
// ---------------------------------------------------------------------------

/**
 * POST /api/auth/local/mfa/enroll/initiate — Requiert partialAuthToken.
 * Retourne l'URI otpauth:// pour afficher le QR code + la clé base32.
 */
export async function initiateMfaEnrollment(
  partialAuthToken: string,
): Promise<MfaEnrollInitiateResponse> {
  const { data } = await localAuthClient.post<MfaEnrollInitiateResponse>(
    "/api/auth/local/mfa/enroll/initiate",
    {},
    { headers: { Authorization: `Bearer ${partialAuthToken}` } },
  );
  return data;
}

/**
 * POST /api/auth/local/mfa/enroll/confirm — Requiert partialAuthToken.
 * Valide le premier code TOTP, retourne les codes de secours + JWT full-access.
 * Les codes de secours doivent être affichés UNE SEULE FOIS.
 */
export async function confirmMfaEnrollment(
  request: MfaEnrollConfirmRequest,
  partialAuthToken: string,
): Promise<MfaEnrollConfirmResponse> {
  const { data } = await localAuthClient.post<MfaEnrollConfirmResponse>(
    "/api/auth/local/mfa/enroll/confirm",
    { totpCode: request.totpCode },
    { headers: { Authorization: `Bearer ${partialAuthToken}` } },
  );
  return data;
}

/**
 * POST /api/auth/local/mfa/reset-request — Anonymous, rate-limited.
 * Demande de réinitialisation MFA soumise à validation admin. Toujours 202 (anti-énumération).
 */
export async function requestMfaRecovery(email: string): Promise<void> {
  await localAuthClient.post("/api/auth/local/mfa/reset-request", { email });
}

// ---------------------------------------------------------------------------
// Déconnexion
// ---------------------------------------------------------------------------

/**
 * POST /api/auth/logout — Requiert Bearer token (full-access).
 * Invalide la session côté serveur (fire-and-forget).
 */
export async function logoutLocalSession(): Promise<void> {
  await apiClient.post("/api/auth/logout");
}

// ---------------------------------------------------------------------------
// Réinitialisation du mot de passe
// ---------------------------------------------------------------------------

/**
 * POST /api/auth/local/password/reset-request — Anonymous, rate-limited.
 * Réponse 202 identique que l'email existe ou non (anti-énumération).
 */
export async function forgotPassword(email: string): Promise<void> {
  await localAuthClient.post("/api/auth/local/password/reset-request", {
    email,
  });
}

/**
 * POST /api/auth/local/password/reset — Anonymous, rate-limited.
 * Requiert l'email EN PLUS du token (tous deux présents dans le lien email).
 * Retourne 204 en cas de succès, 422 si token invalide/expiré ou mdp trop faible.
 */
export async function resetPassword(body: ResetPasswordRequest): Promise<void> {
  await localAuthClient.post("/api/auth/local/password/reset", {
    email: body.email,
    token: body.token,
    newPassword: body.newPassword,
  });
}

/**
 * POST /api/auth/local/password/change — Requiert un accessToken complet
 * (utilisateur authentifié). Changement de mot de passe en libre-service
 * depuis la page Mon compte, pour les comptes locaux uniquement — les
 * comptes Microsoft/Entra n'ont pas de mot de passe géré par cette API.
 * Retourne 204. 403 si l'utilisateur n'a pas de compte local (SSO uniquement),
 * 422 si le mot de passe actuel est incorrect ou si le nouveau ne respecte
 * pas la politique de mot de passe.
 */
export async function changePassword(
  body: ChangePasswordRequest,
): Promise<void> {
  await apiClient.post("/api/auth/local/password/change", {
    currentPassword: body.currentPassword,
    newPassword: body.newPassword,
    confirmNewPassword: body.confirmNewPassword,
  });
}

/**
 * POST /api/auth/local/accept-invite — Anonymous, rate-limited.
 * Active l'invitation et permet a l'utilisateur de definir userName + password.
 */
export async function acceptInvitation(
  body: AcceptInvitationRequest,
): Promise<void> {
  await localAuthClient.post("/api/auth/local/accept-invite", body);
}
